import { parse } from "query-string";
import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router";
import { useWithDefaultParams } from "Services/useWithDefaultParams";
import { generateUrl } from "Services/withDefaultParams";
import { hasOwnProperty } from "Utils/functionalUtils";

const isInitialRange = (initialRange, currentRange) => {
	if (currentRange === null) {
		return true;
	}

	return initialRange.min === currentRange.min && initialRange.max === currentRange.max;
};

const RANGE_TYPE_KEYS = ["gigabytes", "price", "minutes"];
const MAP_TYPE_KEY = ["providers"];

const isSearchParamsEqualFilter = (searchParams, initialFilters, currentFilters) => {
	const filtersAsParams = Object.entries(currentFilters).reduce(
		(accum, [key, value]) => ({
			...accum,
			[key]: typeof value === "object" ? `${value?.min},${value?.max}` : value,
		}),
		{}
	);

	return Object.entries(filtersAsParams).every(([key, value]) => {
		if (searchParams[key]) {
			return value === searchParams[key];
		}

		return isInitialRange(initialFilters[key], currentFilters[key]);
	});
};

export const useFilterParams = () => {
	const { path, params } = useWithDefaultParams();
	const history = useHistory();
	const { search } = useLocation();
	const searchParams = useMemo(() => parse(search), [search]);

	const setFilterParams = useCallback(
		(initialFilters, currentFilters) => {
			const providersUrl = generateUrl(path, {
				regionUrl: params.regionUrl,
				operator: params.operator,
				tagCode: params.tagCode,
			});

			if ("providers" in currentFilters && searchParams.providers !== currentFilters.providers) {
				history.push({
					pathname: providersUrl,
					search: currentFilters.providers?.id ? `?providers=${currentFilters.providers?.id}` : "",
				});

				return;
			}

			const isEqual = isSearchParamsEqualFilter(searchParams, initialFilters, currentFilters);

			if (isEqual) {
				return;
			}

			RANGE_TYPE_KEYS.forEach((key) => {
				if (hasOwnProperty(currentFilters, key)) {
					searchParams[key] = isInitialRange(initialFilters[key], currentFilters[key])
						? ""
						: `${currentFilters[key].min},${currentFilters[key].max}`;
				}
			});

			const filtersArr = Object.entries(searchParams)
				.filter(([param]) => searchParams[param])
				.map(([param, value]) => `${param}=${value}`);

			history.push({
				pathname: providersUrl,
				search: filtersArr.length ? `?${filtersArr.join("&")}` : "",
			});
		},
		[history, params.operator, params.regionUrl, params.tagCode, path, searchParams]
	);

	const filterParams = useMemo(() => {
		const rangesFilters = RANGE_TYPE_KEYS.reduce((accum, key) => {
			if (!searchParams[key]) {
				return accum;
			}
			const [min, max] = searchParams[key].split(",").map(Number);

			return { ...accum, [key]: { min, max } };
		}, {});

		const mapFilters = MAP_TYPE_KEY.reduce((accum, key) => {
			if (!searchParams[key]) {
				return accum;
			}

			return { ...accum, [key]: Number(searchParams[key]) };
		}, {});

		return { ...rangesFilters, ...mapFilters };
	}, [searchParams]);

	const resetFiltersParams = useCallback(() => {
		setFilterParams(null, { providers: "" });
	}, [setFilterParams]);

	return { setFilterParams, filterParams, resetFiltersParams };
};
